import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  getMyAccess,
  listPractitioners,
  listStaffAccess,
  saveMyPractitionerProfile,
  setStaffPermission,
} from "@/lib/sick-notes.functions";
import { SICK_NOTE_PERMISSIONS } from "@/lib/sick-notes";
import { PRACTICE_CREDENTIALS as P } from "@/lib/invoice-details";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/_authenticated/admin/sick-notes/practitioner")({
  head: () => ({ meta: [{ title: "Practitioner Profile · Admin" }, { name: "robots", content: "noindex" }] }),
  component: PractitionerPage,
});

function PractitionerPage() {
  const qc = useQueryClient();
  const accessFn = useServerFn(getMyAccess);
  const practFn = useServerFn(listPractitioners);
  const saveFn = useServerFn(saveMyPractitionerProfile);
  const { data: access } = useQuery({ queryKey: ["sickNoteAccess"], queryFn: () => accessFn() });
  const { data: practitioners, isPending } = useQuery({ queryKey: ["practitioners"], queryFn: () => practFn() });
  const me = practitioners?.find((p: any) => p.is_me);

  const [f, setF] = useState({
    full_name: P.practitioner,
    registration_number: P.hpcsa,
    qualification: P.qualification,
    practice_name: P.name,
    practice_address: P.address,
    phone: P.phone,
    email: P.email,
  });
  const [newSig, setNewSig] = useState<string | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (me) setF({ full_name: me.full_name, registration_number: me.registration_number, qualification: me.qualification ?? "", practice_name: me.practice_name, practice_address: me.practice_address, phone: me.phone ?? "", email: me.email ?? "" });
  }, [me]);

  const canManage = access?.perms.includes("practitioners.manage");

  async function save(removeSig = false) {
    setSaving(true);
    try {
      await saveFn({ data: { ...f, ...(removeSig ? { signature_png: null } : newSig ? { signature_png: newSig } : {}) } });
      toast.success("Practitioner profile saved");
      setNewSig(undefined);
      qc.invalidateQueries({ queryKey: ["practitioners"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (isPending) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="max-w-3xl space-y-4">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">My practitioner profile</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">These details fill in automatically on new sick notes and medical reports. Your stored signature can only be applied by you, while signed in to your own account.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ["full_name", "Full name *"],
                ["registration_number", "Professional registration number *"],
                ["qualification", "Qualification"],
                ["practice_name", "Practice name *"],
                ["practice_address", "Practice address *"],
                ["phone", "Phone"],
                ["email", "Email"],
              ] as const
            ).map(([k, label]) => (
              <div key={k} className={`space-y-1 ${k === "practice_address" ? "sm:col-span-2" : ""}`}>
                <Label className="text-xs text-muted-foreground">{label}</Label>
                <Input disabled={!canManage} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Stored signature</Label>
            {me?.signature_png && newSig === undefined && (
              <div className="flex items-center gap-3">
                <img src={me.signature_png} alt="Your signature" className="h-16 rounded border bg-background p-1" />
                {canManage && <Button size="sm" variant="outline" onClick={() => save(true)} disabled={saving}>Remove</Button>}
              </div>
            )}
            {canManage && (
              <>
                <p className="text-xs text-muted-foreground">{me?.signature_png ? "Draw below to replace it:" : "Draw your signature with your finger or mouse:"}</p>
                <SignaturePad onChange={(s) => setNewSig(s ?? undefined)} />
              </>
            )}
          </div>
          {canManage ? (
            <Button onClick={() => save()} disabled={saving}>{saving ? "Saving..." : "Save profile"}</Button>
          ) : (
            <p className="text-sm text-muted-foreground">You don't have permission to manage practitioner details.</p>
          )}
        </CardContent>
      </Card>

      {access?.admin && <StaffAccess />}
    </div>
  );
}

function StaffAccess() {
  const qc = useQueryClient();
  const listFn = useServerFn(listStaffAccess);
  const setFn = useServerFn(setStaffPermission);
  const { data = [], isPending } = useQuery({ queryKey: ["staffAccess"], queryFn: () => listFn() });
  const toggle = async (user_id: string, permission: string, granted: boolean) => {
    try {
      await setFn({ data: { user_id, permission, granted } });
      qc.invalidateQueries({ queryKey: ["staffAccess"] });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">Staff access to clinical documents</CardTitle></CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">Administrators always have full access. Give reception or other staff only what they need.</p>
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="py-1 pr-2">User</th>
                  {SICK_NOTE_PERMISSIONS.map((p) => <th key={p.key} className="px-1 py-1">{p.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.id} className="border-t">
                    <td className="py-2 pr-2">{u.email}{u.admin && <span className="ml-1 text-muted-foreground">(admin)</span>}</td>
                    {SICK_NOTE_PERMISSIONS.map((p) => (
                      <td key={p.key} className="px-1 py-2 text-center">
                        <Checkbox disabled={u.admin} checked={u.admin || u.perms.includes(p.key)} onCheckedChange={(v) => toggle(u.id, p.key, !!v)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
